import React, { Component } from 'react';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import BlazeTable from 'components/blazeTable/BlazeTable';
import { ApplicationState } from 'store/rootReducer';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';

import { MockServiceAnalytics } from 'types/rest/mockServiceTypes';
import {
  changeStateChangesViewActivePage,
  changeStateChangesViewPageSize,
  setStateChangesViewSearchKeyword,
} from 'store/analytics/ui/subViews/stateChangesView/stateChangesViewActions';
import {
  fetchStateChangesDetailThunk,
  fetchStateChangesThunk,
} from 'store/analytics/data/analyticsStateChangesActions';
import _ from 'lodash';
import MixpanelService from 'utils/mixpanelService';
import { REFRESH_STATE_CHANGES_VIEW, SEARCH_FILTER_RESULTS } from 'utils/mixpanelEvents';
import { StateChangesState, TabDataState } from 'store/analytics/data/analyticsDataTypes';
import { FilterType, StateChangesDetailsType, StateChangesType, TxiViewRequest } from 'types/rest/analyticsTypes';
import { getTableOptions } from 'views/Analytics/AnalyticsMockService/Inspection/InspectionViewMar';
import InspectionHeader from 'views/Analytics/AnalyticsMockService/Inspection/Header/InspectionHeader';
import { getTransaction } from 'store/transactions/transactionsActions';
import { getErrorMessage } from 'utils/miscUtil';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import i18n from 'i18n';
import { formatDateTimeFromSeconds } from 'utils/dateUtil';
import styles from 'views/Analytics/AnalyticsMockService/Inspection/InspectionViewTransactional.module.css';
import { isObjectContainingString } from 'utils/filterUtils';
import { MockServiceAnalyticsState } from 'store/analytics/ui/analyticsUiTypes';
import StateChangesDetails from 'views/Analytics/AnalyticsMockService/StateChanges/StateChangesDetails';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import EditTransactionModal from 'components/modal/EditTransactionModal';
import {
  getCommonDataFromDataContext,
  getCommonDataFromUiContext,
} from 'views/Analytics/AnalyticsMockService/Common/HttpViewUtils';
import EditMessagingModal from '../../../../components/modal/EditMessagingModal';

interface FilteredResults {
  requests: any[];
  tableOptions: BlazeTableOptions;
  sliceData: any;
}

interface StateChangesViewProps {
  mockService: MockServiceAnalytics;
}

/**
 * List of StateChanges
 */
export class StateChangesView extends Component<AllProps> {
  tableRef: any = React.createRef<BlazeTable>();

  state = {
    transaction: null,
  };

  openTxnDetailHandler = async (txnId: string, e: any) => {
    //Do not load analytics detail
    e.stopPropagation();

    //Get txn
    try {
      const txn = (await getTransaction(txnId)).data.result;
      this.setState({ transaction: txn });
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      this.props.showToastNotification(ToastType.Danger, 'Cannot retrieve transaction', 'Reason: ' + errorMsg);
    }
  };

  componentDidMount(): void {
    this.getStateChangesDataForFirstTime();
  }

  componentDidUpdate() {
    this.getStateChangesDataForFirstTime();
  }

  /**
   * Loads data on mount event / tab change when they are not loaded yet.
   */
  getStateChangesDataForFirstTime = () => {
    const contextsAreReady = !_.isNil(this.props.activeDataContext) && !_.isNil(this.props.activeUiContext);
    if (!contextsAreReady) {
      return;
    }
    if (
      this.props.activeUiContext!.activeTab === 'stateChanges' &&
      this.props.activeDataContext.stateChanges.isLoadingData === false &&
      this.props.activeDataContext.stateChanges.lastRefreshTs === null &&
      this.props.activeDataContext.stateChanges.isDetailRequested === false
    ) {
      this.props.getStateChanges(this.props.activeTab);
    }
  };

  callRefresh = () => {
    MixpanelService.track(REFRESH_STATE_CHANGES_VIEW(this.props.activeTab));
    this.props.getStateChanges(this.props.activeTab);
  };

  handleFilterChange = async (value: string) => {
    await this.props.setStateChangesViewSearchKeyword(value);
    this.sendMixPanelEvent();
  };

  sendMixPanelEvent = _.debounce(() => {
    MixpanelService.track(SEARCH_FILTER_RESULTS());
  }, 600);

  /**
   * Gets list of StateChanges from active data context in redux.
   */
  getStateChangesFromDataContext = () => {
    if (_.isNil(this.props.activeDataContext)) {
      return [];
    }
    const txiDataState: StateChangesState = this.props.activeDataContext.stateChanges;
    return (txiDataState.stateChangesTypes as StateChangesType[]) || [];
  };

  getDetail = () =>
    this.getStateChangesDetail(this.props.activeUiContext, this.props.activeDataContext, this.props.getStateChanges);

  // Definition of table columns
  getColsDefinition = (): BlazeTableColDef[] => [
    {
      header: i18n.t('analytics:stateChanges.table.name'),
      width: 200,
      accessor: 'actionName',
    },
    {
      header: i18n.t('analytics:stateChanges.table.type'),
      width: 200,
      accessor: 'objectAction',
    },
    {
      header: i18n.t('analytics:stateChanges.table.dataEntity'),
      width: 200,
      accessor: 'modelEntity',
    },
    {
      header: i18n.t('analytics:stateChanges.table.transactionId'),
      cellComponent: (request: TxiViewRequest) => {
        return (
          <button
            onClick={e => this.openTxnDetailHandler(request.transactionId, e)}
            className={'btn btn-link no-hover'}
            style={{ padding: 0 }}
          >
            {request.transactionId.toString()}
          </button>
        );
      },
      width: 200,
      accessor: 'transactionId',
    },
    {
      header: i18n.t('analytics:stateChanges.table.timestamp'),
      accessor: 'timestamp',
      width: 180,
      cellComponent: (stateChangesType: StateChangesType) => {
        if (_.isNil(stateChangesType.timestamp)) {
          return 'N/A';
        }
        return formatDateTimeFromSeconds(parseInt(stateChangesType.timestamp) / 1000, true);
      },
    },
  ];

  getStateChangesViewTableOptions = (
    onRowClick: any,
    selectedStateChangesType: any,
    paginationOverrides,
  ): BlazeTableOptions => {
    return {
      showRowExpander: false,
      columnDef: this.getColsDefinition(),
      pageable: true,
      selectable: false,
      sortable: true,
      pageSize: 5,
      minWidth: 950,
      minHeight: 210,
      tableName: 'stateChangesList',
      noResultsText: i18n.t('analytics:inspection.noResultsText'),
      customHeaderClassName: 'flex-table-item-top',
      customTableClassName: 'flex-table-simple flex-table-condensed',
      onRowClick: (stateChangesId: number) => onRowClick(stateChangesId),
      customRowClassName: (request: any) => {
        let rowClass = 'hand-cursor ';
        if (selectedStateChangesType && request.id.toString() === selectedStateChangesType.id.toString()) {
          rowClass += styles.customEventClass;
        }
        return rowClass;
      },
      maxContentHeight: '186px',
      paginationOverride: paginationOverrides,
      hidePager: paginationOverrides.total === 0,
    };
  };

  getFilteredStateChangesType = (searchKeyword: string, stateChangesItems: any[]) => {
    if (!_.isEmpty(searchKeyword)) {
      const fieldsToSearch = ['id', 'actionName', 'objectAction', 'modelEntity', 'transactionId', 'timestamp'];
      return stateChangesItems.filter((item: StateChangesType) => {
        return isObjectContainingString(searchKeyword, item, fieldsToSearch);
      });
    } else {
      return stateChangesItems;
    }
  };

  getStateChangesDetail = (
    activeUiContext: MockServiceAnalyticsState,
    activeDataContext: TabDataState,
    onRefreshHandler: (mockServiceId: string | number) => void,
  ) => {
    const selectedStateChangesType = activeUiContext.stateChangesView.selectedStateChangesType as StateChangesType;

    const tabId = activeDataContext.tabId;
    const stateChanges = activeDataContext.stateChanges;
    const isOutdated = stateChanges ? stateChanges.isOutdated : false;
    const details = stateChanges.detail ? (stateChanges.detail as StateChangesDetailsType[]) : [];
    const changedRowsCount = stateChanges.changedRowsCount ? stateChanges.changedRowsCount : 0;
    const filters = stateChanges.filters ? (stateChanges.filters as FilterType[]) : [];
    const isLoading = stateChanges ? stateChanges.isLoadingDetails : false;

    return (
      <StateChangesDetails
        tabId={tabId}
        isLoading={isLoading}
        changedRowsCount={changedRowsCount}
        filters={filters}
        details={details}
        isOutdated={isOutdated}
        onRefresh={onRefreshHandler}
        stateChangesName={selectedStateChangesType.actionName}
      />
    );
  };

  render() {
    // UI state related data
    const { activePage, pageSize, searchKeyword } = getCommonDataFromUiContext(
      this.props.activeUiContext,
      'stateChangesView',
    );
    const selectedStateChangesType = this.props.activeUiContext?.stateChangesView?.selectedStateChangesType
      ? this.props.activeUiContext.stateChangesView.selectedStateChangesType
      : null;

    // Data state
    const { isRequestListLoading, lastRefresh, tabId } = getCommonDataFromDataContext(
      this.props.activeDataContext,
      'stateChanges',
    );
    const isStateChangesListLoading = isRequestListLoading;

    const stateChangesTypes = this.getStateChangesFromDataContext();
    const initResult = this.getFilteredStateChangesType(searchKeyword, this.getStateChangesFromDataContext());

    const tableOptions: BlazeTableOptions = this.getStateChangesViewTableOptions(
      reqId => this.props.getDetail(reqId, tabId),
      selectedStateChangesType,
      {
        pageSize: pageSize,
        total: stateChangesTypes.length,
        activePage: activePage,
      },
    );
    const filterResult: FilteredResults = getTableOptions(initResult, searchKeyword, tableOptions, pageSize);

    return (
      <>
        {!_.isNil(this.state.transaction) &&
          (this.state.transaction.type === 'HTTP' ? (
            <EditTransactionModal
              isReadOnly={false}
              isNew={false}
              transaction={this.state.transaction as any}
              onClose={() => {
                this.setState({ transaction: null });
              }}
            ></EditTransactionModal>
          ) : (
            <EditMessagingModal
              isReadOnly={false}
              isNew={false}
              transaction={this.state.transaction as any}
              onClose={() => {
                this.setState({ transaction: null });
              }}
            ></EditMessagingModal>
          ))}
        <InspectionHeader
          refreshHandler={this.callRefresh}
          requests={stateChangesTypes}
          onSearchKeywordChanged={this.handleFilterChange}
          lastRefresh={lastRefresh}
          searchKeyword={searchKeyword}
          title={i18n.t('analytics:stateChanges.title')}
          searchPlaceHolder={i18n.t('analytics:stateChanges.searchPlaceholder')}
          msType={this.props.mockService.type}
        ></InspectionHeader>
        <BlazeTable
          isLoading={isStateChangesListLoading}
          options={filterResult.tableOptions}
          onPageSizeChange={this.props.changeStateChangesViewPageSize}
          onPageChange={this.props.changeStateChangesViewActivePage}
          data={filterResult.sliceData}
          onRef={(ref: any) => (this.tableRef = ref)}
          showPageSizeDropdown={true}
        />

        {/* Detail for selected StateChanges, show it only if there is detail selected */}
        {!_.isNil(selectedStateChangesType) && !_.isNil(lastRefresh) && this.getDetail()}
      </>
    );
  }
}

const mapStateToProps = ({ analytics }: ApplicationState) => {
  return {
    activeTab: analytics.ui.activeTab,
    activeUiContext: analytics.ui.activeUiContext,
    activeDataContext: analytics.data.activeDataContext,
  };
};

const mapDispatchToProps = {
  changeStateChangesViewPageSize,
  changeStateChangesViewActivePage,
  setStateChangesViewSearchKeyword,
  getDetail: fetchStateChangesDetailThunk,
  getStateChanges: fetchStateChangesThunk,
  showToastNotification,
};

type MapStateToProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
type AllProps = MapStateToProps & WithTranslation & DispatchProps & StateChangesViewProps;

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(StateChangesView));
